import _ from 'lodash';
import initialize, { interceptAxios, handleLogout } from '@patsnap-utils/passport';
import { psAxios, setPostByFormPassport } from '@patsnap/common-ps-http';
import { transformUrlProxy, SLD, Site } from '@patsnap-utils/biz';
import { applySignInterceptors } from './sign';
import { isAnonymousError } from '../../exception';
import { prepareTokenDeferred } from '../account';
import type { AxiosRequestConfig, AxiosError } from 'axios';
import type { ISyncTokenParams } from '@patsnap-utils/passport/src/type';
// TODO: 移除 tahiti-core 依赖 - 需要替换 injectService
// import { injectService } from '@patsnap/tahiti-core';
// import { UnauthenticatedHandler } from '../services/unauthenticated-handler/unauthenticated-handler';

export interface IProjectPreset {
    name: string
    config?: AxiosRequestConfig
    apiVersion?: string
    excludesUrlRegExps?: any[]
    callback?: (axios: typeof psAxios) => unknown
    preRedirectLogin?: (error: unknown) => unknown
    region?: string
}

/**
 * 延迟执行辅助函数
 */
function sleep(timeout: number) {
    return new Promise((resolve) => {
        setTimeout(resolve, timeout);
    });
}

const PASSPORT_URI = import.meta.env.VITE_PASSPORT_URI;
const CLIENT_ID = import.meta.env.VITE_PASSPORT_CLIENT_ID;
const CLIENT_NAME = import.meta.env.VITE_PASSPORT_CLIENT_NAME;

const baseAuthServer = transformUrlProxy(SLD.PASSPORT, PASSPORT_URI as string);

let _header = {};

const passportInterceptManager = {
    /**
     * 注入 passport 拦截器
     * @description 如果需要使用 token base，则需要调用此方法
     */
    inject() {
        if (!psAxios.psInterceptors.passportRequestInterceptor) {
            interceptAxios(psAxios);
        }
    },
    /**
     * 移除 passport 拦截器
     * @description 如果不需要使用 token base，则需要调用此方法
     */
    eject() {
        psAxios.interceptors.request.eject(psAxios.psInterceptors.passportRequestInterceptor);
        psAxios.interceptors.response.eject(psAxios.psInterceptors.passportResponseInterceptor);
        delete psAxios.psInterceptors.passportRequestInterceptor;
        delete psAxios.psInterceptors.passportResponseInterceptor;
    },
};

function stopper() {
    if (window.location.search.indexOf('noraven') > -1) {
        throw new Error('Local stop redirect');
    }
}

function revertString(str:string) {
    return str.split('').reverse().join('');
}

function redirectToUserSettings(page: string, error: any) {
    const urlParams = {
        from: CLIENT_NAME,
        client_id: CLIENT_ID,
        redirect_uri: encodeURIComponent(window.location.href),
        errorCode: _.get(error, 'errcode'),
    };
    const urlSearchParams = new URLSearchParams(urlParams);
    const url = new Site({
        sld: SLD.ACCOUNT,
        pathname: `user-settings/#/${page}?${urlSearchParams.toString()}`,
    }).href;
    stopper();
    window.location.replace(url);
}

function createInitializeConfig(preset?: IProjectPreset) {
    return {
        baseAuthServer,
        clientId: CLIENT_ID,
        clientName: CLIENT_NAME,
        redirectToLogin(error: AxiosError) {
            // 先调用 preRedirectLogin 钩子（如果存在）
            // 如果抛出错误（如 AnonymousError），会阻止后续的跳转逻辑
            if (preset?.preRedirectLogin) {
                preset.preRedirectLogin(error);
            }

            console.error('Unauthenticated error, redirecting to login...', error);

            // 构建登录页 URL
            const searchParams = new URLSearchParams({
                from: CLIENT_NAME,
                client_id: CLIENT_ID,
                redirect_uri: window.location.href,
            }).toString();

            const loginUrl = new Site({
                sld: SLD.PASSPORT,
                pathname: `login?${searchParams}`,
            }).href;

            // 跳转到登录页
            stopper();
            window.location.href = loginUrl;

            // 正在跳转（大概6秒够了），后面的别执行了
            return sleep(6000);
        },
        /*  可选，允许修改 check session 的参数 */
        syncTokenParamsModifier(params: ISyncTokenParams) {
            return { ...params /* , flag: 'analytics', */ };
        },
        detectResponseAsUnauthorized(error: AxiosError) {
            const redirectUrl = _.get(error, 'response.data.redirectUrl');
            if (redirectUrl) {
                stopper();
                window.location.href = redirectUrl;
                throw error;
            }
            return _.get(error, 'response.status') === 401;
        },
        onPaymentRequired(error: any) {
            redirectToUserSettings('upper-limit', error);
        },
        async onMfaRequired(error: any) {
            redirectToUserSettings('mfa-required', error);
        },
    };
}

async function prepareTokenBase(preset?: IProjectPreset) {
    const initializeConfig = createInitializeConfig(preset);

    function interceptorRequestSuccess(config: AxiosRequestConfig) {
        if (config.headers) {
            config.headers['X-PatSnap-From'] = `w-${CLIENT_NAME}`;
        }
        config.headers = {
            ...config.headers,
            ..._header,
        };

        return config;
    }

    psAxios.psInterceptors.projectRequestInterceptor = psAxios.interceptors.request.use(interceptorRequestSuccess, undefined);
    /* 清除 csrfTokenInterceptor */
    psAxios.interceptors.request.eject(psAxios.psInterceptors.csrfTokenInterceptor);

    passportInterceptManager.inject();

    window.customBeforeLogout = function customBeforeLogout() {
        // 此方法默认会被 Header V4点"登出"时调用， 用其他头时，登出时自己调用 handleLogout()
        return handleLogout();
    };

    try {
        const passport = await initialize(initializeConfig as any);

        _.set(window, revertString('6csg9md32h'), function() {
            return passport.getToken('token') || '';
        });

        applySignInterceptors(psAxios);
        setPostByFormPassport(passport);

        // JWT Header 拦截器：解析 token 并添加自定义 header
        function addionJwtHeaderInterceptor(config: AxiosRequestConfig) {
            try {
                const token = passport.getToken('token');
                if (!token) {
                    return config;
                }

                // 解析 JWT token（Base64 解码）
                const parts = token.split('.');
                if (parts.length !== 3) {
                    console.warn('Invalid JWT token format');
                    return config;
                }

                // 解码 payload（第二部分）
                const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
                const decodedPayload = decodeURIComponent(
                    Array.prototype.map.call(
                        window.atob(payload),
                        (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
                    ).join(''),
                );
                const jwt = JSON.parse(decodedPayload);

                // 提取用户信息
                const { sub = '', authorities = [], tenant_id = '', client_id = '' } = jwt;

                // 添加自定义 header
                const addionHeaders: Record<string, any> = {
                    'X-User-ID': sub,
                    'X-User-Roles': authorities,
                    'X-Tenant-ID': tenant_id,
                    'X-Client-ID': client_id,
                };

                config.headers = _.merge({}, config.headers, addionHeaders);
            } catch (err) {
                console.error('Parse JWT error:', err);
            }
            return config;
        }

        // 根据配置决定是否添加 JWT header 拦截器
        // VITE_USE_LOCAL_BACKEND=1 时添加（用于本地开发直接对接后端）
        const useLocalBackend = import.meta.env.VITE_USE_LOCAL_BACKEND === '1';
        if (useLocalBackend) {
            psAxios.interceptors.request.use(addionJwtHeaderInterceptor);
        }

        // 403 错误重试拦截器：处理 token 过期导致的权限错误
        psAxios.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const status = error.response?.status;
                const errorMessage = _.get(error, 'response.data.message', '').toLowerCase();
                const config = error.config as AxiosRequestConfig & { _retryCount?: number };

                // 检查是否是权限相关的 403 错误
                const isPermissionError = status === 403 && (
                    errorMessage.includes('project not found') ||
                    errorMessage.includes('access denied') ||
                    errorMessage.includes('permission denied') ||
                    errorMessage.includes('no access') ||
                    errorMessage.includes('无权访问') ||
                    errorMessage.includes('权限')
                );

                // 如果是权限错误且未重试过，尝试刷新 token 后重试
                if (isPermissionError && !config._retryCount) {
                    console.log('[403 Retry] Detected permission error, attempting token refresh and retry');

                    try {
                        // 标记已重试，避免无限循环
                        config._retryCount = 1;

                        // 等待一小段时间让 passport 刷新 token
                        await sleep(1000);

                        // 重新获取 token（passport 可能已自动刷新）
                        const newToken = passport.getToken('token');
                        if (newToken && config.headers) {
                            config.headers['Authorization'] = `Bearer ${newToken}`;

                            // 如果使用本地后端，重新添加 JWT headers
                            if (useLocalBackend) {
                                const updatedConfig = addionJwtHeaderInterceptor(config);
                                console.log('[403 Retry] Retrying request with refreshed token');
                                return psAxios.request(updatedConfig);
                            }
                        }

                        console.log('[403 Retry] Retrying request');
                        return psAxios.request(config);
                    } catch (retryError) {
                        console.error('[403 Retry] Retry failed:', retryError);
                        // 重试失败，抛出原始错误
                        return Promise.reject(error);
                    }
                }

                // 不是权限错误或已重试过，直接抛出错误
                return Promise.reject(error);
            },
        );

        // 设置 prepareTokenDeferred，所有订阅了该 deferred 的地方会自动更新
        prepareTokenDeferred.resolve(passport);

        return passport;
    } catch (e) {
        // 如果是 AnonymousError，表示用户选择留在当前页面（未登录状态）
        // 移除 passport 拦截器，允许匿名访问
        if (isAnonymousError(e)) {
            passportInterceptManager.eject();
            // 设置 prepareTokenDeferred 为 null，表示未登录
            prepareTokenDeferred.resolve(null as any);
            // 不再抛出错误，允许应用继续初始化
            return null;
        }
        // 其他错误继续抛出
        throw e;
    }
}

/**
 * 更新 token base 请求的 header， 如 X-Site-Lang
 * @param headers
 */
function updateTokenBaseRequestHeaders(headers: Record<string, unknown>): void {
    _header = headers;
}

export {
    prepareTokenBase,
    updateTokenBaseRequestHeaders,
    passportInterceptManager,
};
